import {Injectable, NgZone} from '@angular/core';
import { User } from "src/models/user.model";
import { AngularFirestore, AngularFirestoreDocument } from '@angular/fire/firestore';
import { Router } from "@angular/router";
import { AngularFireAuth } from "@angular/fire/auth";
import {Observable, of} from "rxjs";
import { switchMap, take } from 'rxjs/operators';
import firebase from "firebase/app";
import { Account } from 'src/models/account.model';
import {AccountService} from "./account.service";
import {UserService} from "./user.service";
import {AuditService} from "./audit.service";
import {AuditActionType} from "../models/audit.model";
import { RouteHelperService } from './route-helper.service';
import { environment } from '../environments/environment';

enum UserRole {
  SuperAdmin = 'superAdmin',
  Admin = 'admin',
  Editor = 'editor',
  Subscriber = 'subscriber',
  Standard = 'standard'
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  userData: any; // Save logged in user data
  user$: Observable<User>;

  accounts: Account[] = [];

  constructor(
    public afs: AngularFirestore,
    public afAuth: AngularFireAuth,
    public router: Router,
    private accountService: AccountService,
    private userService: UserService,
    private auditService: AuditService,
    private routeHelper: RouteHelperService
  ) {

    /* Saving user data in localstorage when
    logged in and setting up null when logged out */

    this.afAuth.authState.subscribe(user => {
      if (user) {
        this.userData = user;
        localStorage.setItem('user', JSON.stringify(this.userData));
        JSON.parse(localStorage.getItem('user'));
      } else {
        localStorage.setItem('user', null);
        JSON.parse(localStorage.getItem('user'));
      }
    })

    //// Get auth data, then get firestore user document || null
    this.user$ = this.afAuth.authState.pipe(
      switchMap(user => {
        if (user) {
          return this.afs.doc<User>(`users/${user.uid}`).valueChanges()
        } else {
          return of(null)
        }
      })
    )

  }

  // Sign in with email/password
  async signIn(email, password) {
    return this.afAuth.signInWithEmailAndPassword(email, password)
      .then(async (result) => {
        // Log successful login
        await this.auditService.logAuthentication(AuditActionType.LOGIN, {
          user_email: result.user.email,
          user_name: result.user.displayName,
          notes: `User logged in via email/password`
        });

        this.getUserById(result.user.uid).pipe(take(1)).subscribe((u) =>{

          if(u && u.roles && u.roles.superAdmin){
            let user = { user_id: result.user.uid, timestamp: new Date()}
            this.updateUserWithLogActivity(user);
            // Store full user object with roles
            localStorage.setItem('user', JSON.stringify(result.user));
            localStorage.setItem('currentUser', JSON.stringify({
              id: result.user.uid,
              username: u.fullName || result.user.displayName || result.user.email,
              fullname: u.fullName || result.user.displayName,
              role: u.roles.superAdmin ? 'superadmin' : 'admin',
              roles: u.roles,
              email: result.user.email,
              token: null
            }));
            setTimeout(() => {
              this.router.navigate(['/admin/accounts']);
            }, 100);
          }else{

            this.checkIfUserHasAccount(result.user.uid).then((account_user) =>{
              this.addUserLogActivity(account_user);
              if(account_user && account_user.account_key && account_user.id){
                this.getAccountByKey(account_user.account_key).then((account) =>{
                  // Store full user object with roles
                  localStorage.setItem('user', JSON.stringify(result.user));
                  localStorage.setItem('account', JSON.stringify(account));
                  // Store currentUser with role information
                  const userRole = u.roles?.admin ? 'admin' : u.roles?.editor ? 'editor' : 'user';
                  localStorage.setItem('currentUser', JSON.stringify({
                    id: result.user.uid,
                    username: u.fullName || result.user.displayName || result.user.email,
                    fullname: u.fullName || result.user.displayName,
                    role: userRole,
                    roles: u.roles,
                    email: result.user.email,
                    account_key: account_user.account_key,
                    token: null
                  }));
                  setTimeout(() => {
                    this.router.navigate([account_user.account_key + '/user/dashboard']);
                  }, 100);
                });
              }else{
                alert("Sorry, user has no assigned account. Please contact your administrator.");
                setTimeout(() => {
                  this.router.navigate(['/auth/login']);
                }, 100);
              }

            }, (error) =>{
              alert("Sorry, user has no assigned account. Please contact your administrator.");
              setTimeout(() => {
                this.router.navigate(['/auth/login']);
              }, 100);
            });

          }

        });

      }).catch(async (error) => {
        // Log failed login attempt
        await this.auditService.logAuthentication(AuditActionType.LOGIN_FAILED, {
          user_email: email,
          notes: `Failed login attempt: ${error.message}`
        });
        alert(error.message);
        window.location.reload();
      })
  }

  //signIn with phone
  signInWithPhone(phone: string, recaptchaVerifier: any){
    this.afAuth.signInWithPhoneNumber(phone, recaptchaVerifier).then( (res) => {
      localStorage.setItem('verificationId', JSON.stringify(res.verificationId));
      localStorage.setItem('phoneNumber', JSON.stringify(phone));
      this.router.navigate(['auth/verify-otp']);
    }).catch(async (error) =>{
      // Log failed phone login attempt (OTP request failed)
      await this.auditService.logAuthentication(AuditActionType.LOGIN_FAILED, {
        user_email: phone,
        notes: `Failed to send OTP to phone: ${error.message}`
      });
      alert(error.message);
      setTimeout(() =>{
        window.location.reload();
      }, 3000)
    });
  }

  async phoneAuthVerification(verify, otpCode, phoneNumber): Promise<any>{
    return new Promise((resolve, reject) => {
      let credentials = firebase.auth.PhoneAuthProvider.credential( verify, otpCode)
      firebase.auth().signInWithCredential(credentials).then(async (result: any) => {

          // Log successful phone login
        await this.auditService.logAuthentication(AuditActionType.LOGIN, {
          user_email: result.user.email || phoneNumber,
          user_name: result.user.displayName || 'Phone User',
          notes: `User logged in via phone authentication: ${phoneNumber}`
        });

        
        // Check if this is a public user (mystreetparking context)
        const isPublicUser = this.isPublicUserContext();

        if (isPublicUser) {
          // For public users, just resolve without account checks
          result.user.phoneNumberVerified = true;
          resolve(result.user);
          return;
        }

        // For admin/employee users, check account assignments
        this.checkUserPhoneIfExist(phoneNumber).then(async (res: any) => {
          if (!res) {
            result.user.phoneNumberVerified = true;
            let userData = await this.setUserData("", result.user);
            await this.loginUserByPhone(userData);
            resolve(result.user);
          }else{
            await this.loginUserByPhone(res);
            resolve(res);
          }

        });
      }).catch(async (error) => {
        // Log failed phone login attempt
        await this.auditService.logAuthentication(AuditActionType.LOGIN_FAILED, {
          user_email: phoneNumber,
          notes: `Failed phone authentication: ${error.message}`
        });
        resolve(error);
      });
    });
  }

  async loginUserByPhone(user) {
    // Safety check: never call this method for public users
    if (this.isPublicUserContext()) {
      return;
    }

    if (user.roles.admin && user.roles.editor) {
      let userLog = {user_id: user.uid, timestamp: new Date()}
      await this.updateUserWithLogActivity(userLog);
      localStorage.setItem('user', JSON.stringify(user));
      // Store currentUser with role information
      localStorage.setItem('currentUser', JSON.stringify({
        id: user.uid,
        username: user.fullName || user.displayName || user.email || user.phoneNumber,
        fullname: user.fullName || user.displayName,
        role: user.roles.superAdmin ? 'superadmin' : 'admin',
        roles: user.roles,
        email: user.email,
        phoneNumber: user.phoneNumber,
        token: null
      }));
      await this.router.navigate(['/admin/accounts']);
    } else {

      this.checkIfUserHasAccount(user.uid).then((account_user) => {
        this.addUserLogActivity(account_user);
        if (account_user && account_user.account_key && account_user.id) {
          this.getAccountByKey(account_user.account_key).then((account) => {
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('account', JSON.stringify(account));
            // Store currentUser with role information
            const userRole = user.roles?.admin ? 'admin' : user.roles?.editor ? 'editor' : 'user';
            localStorage.setItem('currentUser', JSON.stringify({
              id: user.uid,
              username: user.fullName || user.displayName || user.email || user.phoneNumber,
              fullname: user.fullName || user.displayName,
              role: userRole,
              roles: user.roles,
              email: user.email,
              phoneNumber: user.phoneNumber,
              account_key: account_user.account_key,
              token: null
            }));
            this.router.navigate([account_user.account_key + '/user/profile']);
          });
        } else {
          alert("Sorry, user has no assigned account. Please contact your administrator.");
          this.router.navigate(['/auth/login']);
        }

      });

    }
  }

  // Sign up with email/password
  async signUp(name, email, password): Promise<any> {
    return new Promise((resolve, reject) => {
      this.afAuth.createUserWithEmailAndPassword(email, password)
        .then((result) => {
          /* Call the SendVerificationMail() function when new user sign
          up and returns promise */
          this.sendVerificationMail();
          this.setUserData(name, result.user);
          //this.router.navigate(['/auth/login']);
          resolve(result);
        }).catch((error) => {
        reject(error);
      });
    });
  }

  // Send email verification when new user sign up
  async sendVerificationMail() {
    return firebase.auth().currentUser.sendEmailVerification()
      .then(() => {
        this.router.navigate(['/auth/verify-email']);
      })
  }

  // Reset Forgot password
  async forgotPassword(passwordResetEmail): Promise<any> {
    return new Promise((resolve, reject) => {
      this.afAuth.sendPasswordResetEmail(passwordResetEmail)
        .then((res) => {
          resolve(true);
        }).catch((error) => {
          reject(error);
          //window.alert(error)
        })
    });
  }

  // Returns true when user is logged in and email is verified
  // get isLoggedIn(): boolean {
  //   const user = JSON.parse(localStorage.getItem('user'));
  //   return (user !== null && user.emailVerified !== false);
  // }

  get isLoggedIn(): boolean {
    const user = JSON.parse(localStorage.getItem('user'));
    return user !== null && (user.phoneNumberVerified !== false || user.emailVerified !== false);
  }

  // Sign in with Google
  googleAuth() {
    return this.authLogin(new firebase.auth.GoogleAuthProvider());
  }

  // Auth logic to run auth providers
  async authLogin(provider) {
    return this.afAuth.signInWithPopup(provider)
      .then(async (result) => {
        // Log successful social login (Google)
        await this.auditService.logAuthentication(AuditActionType.LOGIN, {
          user_email: result.user.email,
          user_name: result.user.displayName,
          notes: `User logged in via Google authentication`
        });
        this.router.navigate(['/admin/dashboard']);
        this.setUserData(result.user.displayName, result.user);
      }).catch(async (error) => {
        // Log failed social login attempt
        await this.auditService.logAuthentication(AuditActionType.LOGIN_FAILED, {
          notes: `Failed Google authentication: ${error.message}`
        });
        window.alert(error)
      })
  }

  /* Setting up user data when sign in with username/password,
  sign up with username/password and sign in with social auth
  provider in Firestore database using AngularFirestore + AngularFirestoreDocument service */
  async setUserData(name, u) {
    return new Promise((resolve, reject) => {
      const userRef: AngularFirestoreDocument<any> = this.afs.doc(`users/${u.uid}`);
      const data = {
        uid: u.uid,
        email: u.email,
        fullName: name,
        displayName: u.displayName,
        photoURL: u.photoURL,
        emailVerified: u.emailVerified,
        phoneNumber: u.phoneNumber || '',
        phoneNumberVerified: u.phoneNumberVerified || false,
        timestamp: new Date(),
        roles: {
          standard: true,
          subscriber: true
        }
      }

      return userRef.set(data, {
        merge: true
      }).then(() =>{
        resolve(data);
      });

    });
  }

  canRead(user: User): boolean {
    const allowedRoles: UserRole[] = [
      UserRole.SuperAdmin,
      UserRole.Admin,
      UserRole.Editor,
      UserRole.Subscriber
    ]
    return AuthService.checkAuthorization(user, allowedRoles)
  }

  canEdit(user: User): boolean {
    const allowedRoles: UserRole[] = [
      UserRole.SuperAdmin,
      UserRole.Admin,
      UserRole.Editor
    ]
    return AuthService.checkAuthorization(user, allowedRoles)
  }

  isAdminCanEdit(user: User): boolean {
    const allowedRoles: UserRole[] = [
      UserRole.SuperAdmin,
      UserRole.Admin
    ]
    return AuthService.checkAuthorization(user, allowedRoles)
  }

  canDelete(user: User): boolean {
    const allowedRoles: UserRole[] = [
      UserRole.SuperAdmin
    ]
    return AuthService.checkAuthorization(user, allowedRoles)
  }

  isEditor(user: User): boolean {
    const allowedRoles: UserRole[] = [
      UserRole.Editor
    ]
    return AuthService.checkAuthorization(user, allowedRoles)
  }

  isSubscriber(user: User): boolean {
    const allowedRoles: UserRole[] = [
      UserRole.Subscriber
    ]
    return AuthService.checkAuthorization(user, allowedRoles)
  }

  isSuperAdmin(user: User): boolean {
    const allowedRoles: UserRole[] = [
      UserRole.SuperAdmin
    ]
    return AuthService.checkAuthorization(user, allowedRoles)
  }

// determines if user has matching role
  private static checkAuthorization(user: User, allowedRoles: string[]): boolean {
    if(!user) return false;
    for(const role of allowedRoles){
      if(user.roles[role]){
        return true;
      }
    }
    return false;
  }

  // Sign out
  async signOut() {
    // Log logout before signing out
    await this.auditService.logAuthentication(AuditActionType.LOGOUT, {
      notes: 'User logged out'
    });

    return this.afAuth.signOut().then(() => {
      localStorage.removeItem('user');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('account');
      localStorage.removeItem('verificationId');
      localStorage.removeItem('phoneNumber');

      // Check if we're in a public context and redirect appropriately
      if (this.isPublicUserContext()) {
        this.router.navigate([this.routeHelper.getRoutes().auth]);
      } else {
        this.router.navigate(['/auth/login']);
      }
    })
  }

  // User service methods moved here to break circular dependency
  getUserById(id: string): Observable<any> {
    return this.afs.doc<any>(`users/${id}`).valueChanges();
  }

  updateUserWithLogActivity(user: any): Promise<void> {
    const userRef = this.afs.doc(`users/${user.user_id}`);
    return userRef.update({
      lastLogin: user.timestamp
    });
  }

  addUserLogActivity(accountUser: any): Promise<void> {
    if (!accountUser || !accountUser.id) {
      return Promise.resolve();
    }

    const logRef = this.afs.collection('user_logs');
    return logRef.add({
      user_id: accountUser.id,
      account_key: accountUser.account_key,
      timestamp: new Date(),
      activity: 'login'
    }).then(() => {});
  }

  checkUserPhoneIfExist(phoneNumber: string): Promise<any> {
    return this.afs.collection('users', ref => ref.where('phoneNumber', '==', phoneNumber))
      .valueChanges()
      .pipe(take(1))
      .toPromise()
      .then((users: any[]) => {
        return users && users.length > 0 ? users[0] : null;
      });
  }

  // Account service methods moved here to break circular dependency
  async checkIfUserHasAccount(id: string): Promise<any> {
    try {
      const snapshots = await firebase.firestore().collection('account_users').get();

      if (snapshots.empty) {
        return null;
      }

      // Early exit optimization - find user as soon as we find one
      for (const doc of snapshots.docs) {
        const data = doc.data();
        if (data.users && Array.isArray(data.users)) {
          const foundUser = data.users.find((user: any) => user.id === id);
          if (foundUser) {
            return foundUser;
          }
        }
      }

      return null;
    } catch (error) {
      throw error;
    }
  }

  async getAccountByKey(accountKey: string): Promise<any> {
    return this.afs.collection('accounts', ref => ref.where('account_key', '==', accountKey))
      .valueChanges()
      .pipe(take(1))
      .toPromise()
      .then((accounts: any[]) => {
        return accounts && accounts.length > 0 ? accounts[0] : null;
      });
  }

  /**
   * Determine if we're in a public user context (MyParking)
   * This works regardless of whether routes are at root level or under /myparking prefix
   */
  private isPublicUserContext(): boolean {
    const currentUrl = this.router.url;
    
    // Always check for /myparking prefix first (this covers non-MyParking deployment)
    if (currentUrl.includes('/myparking')) {
      return true;
    }
    
    // If we're using MyParking deployment (routes at root), check for specific public routes
    if (environment.isMyParkingDeployment) {
      const publicRoutes = ['/login', '/verify-otp', '/dashboard', '/vehicle-registration', '/payment-status', '/'];
      return publicRoutes.some(route => currentUrl === route || currentUrl.startsWith(route + '/'));
    }
    
    return false;
  }

}
